import ScrollModal from "@/components/common/scroll-modal";
import { useUIStore } from "../store/uiStore";
import { Button, Heading, Icon, PageTabs } from "@elaniintc/goes-design-system";
import { useWorkflowStore } from "../store/workflowStore";
import { useState } from "react";
import FormSimpleContent from "./simple-content/form";
import type { NodeData, RefName } from "../types/workflow";
import { cn } from "@/utils/theme";
import RevisionSimpleContent from "./simple-content/revision";
import IfSimpleContent from "./simple-content/if";
import SwitchSimpleContent from "./simple-content/switch";
import DataCenterContent from "./simple-content/data-center";
import DocumentGeneratorContent from "./simple-content/document-generator";
import NotificationSimpleContent from "./simple-content/notification";
import CalculatedSimpleContent from "./simple-content/calculated";
import AssignGroupContent from "./simple-content/asignacion";
import ResolutionContent from "./simple-content/resolution";
import ExpedienteSimpleContent from "./simple-content/expediente";

const tabs = [
  { key: "simple", label: "Simple" },
  { key: "raw", label: "Avanzado" },
];

const ExecutionDetailsModal = () => {
  const [selectedTab, setSelectedTab] = useState<"raw" | "simple">("simple");
  const { executionDetailsOpen, setExecutionDetailsOpen } = useUIStore();
  const { executionData, selectedNodeId, getNodeById } = useWorkflowStore();
  const selectedNode = getNodeById(selectedNodeId!);
  const nodeType = (selectedNode?.data as unknown as NodeData).type as RefName;
  const formalityRequest = executionData?.formality_requests?.[0];

  const nodeExecutions = executionData?.node_executions
    ?.filter(({ node_id }) => {
      return node_id === selectedNodeId;
    })
    .sort((a, b) => {
      // if ended_at is null, put it last
      if (a.ended_at === null) return 1;
      if (b.ended_at === null) return -1;
      // sort by ended_at descending
      return new Date(b.ended_at).getTime() - new Date(a.ended_at).getTime();
    });

  const currentExecution = nodeExecutions?.[0];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(nodeExecutions, null, "\t"),
      );
    } catch (e) {
      console.error("Failed to copy text:", e);
    }
  };

  return (
    <ScrollModal
      isOpen={executionDetailsOpen!}
      onClose={() => {
        setExecutionDetailsOpen(false);
      }}
      modalContentClassName="h-full max-w-[800px] max-h-[800px] rounded-xl"
      modalChildrenClassName="overflow-hidden"
      header={
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-center !text-h2 font-semibold text-heading">
              <Heading variant="h5">Detalles de la ejecución</Heading>
            </div>
            <Button
              variant="quiet"
              size="xs"
              onClick={() => {
                setExecutionDetailsOpen(false);
              }}
              className="min-w-9 px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="p-6 pt-4">
          <div
            className={cn(
              "w-full flex justify-between items-center",
              selectedTab !== "raw" && "justify-end",
            )}
          >
            {selectedTab === "raw" && (
              <Button
                variant="tertiary"
                type="button"
                onClick={() => {
                  handleCopy();
                }}
                size="icon"
                aria-label="Copiar JSON"
              >
                <Icon type="far" name="copy" className="text-sm" />
              </Button>
            )}
            <Button
              onClick={() => {
                setExecutionDetailsOpen(false);
              }}
              size="lg"
              type="button"
            >
              Cerrar
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col w-full h-full">
        <PageTabs
          activeKey={selectedTab}
          onTabChange={(key: string) => setSelectedTab(key as "raw" | "simple")}
          tabs={tabs}
        />
        <div
          className="flex-1 px-10 py-4 text-body overflow-y-auto"
          //className="w-full overflow-y-auto"
        >
          {selectedTab === "raw" && (
            <>
              {nodeExecutions && nodeExecutions.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-body">
                  No hay datos de ejecución para este nodo.
                </div>
              ) : (
                <pre className="text-body">
                  {JSON.stringify(nodeExecutions, null, "\t")}
                </pre>
              )}
            </>
          )}
          {selectedTab === "simple" && (
            <>
              {(nodeExecutions && nodeExecutions.length === 0) ||
              currentExecution?.outputs.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-body">
                  No hay datos de ejecución para este nodo.
                </div>
              ) : (
                <>
                  {nodeType === "form-node" && (
                    <FormSimpleContent
                      execution={currentExecution!}
                      formId={currentExecution?.inputs[0].value as string}
                      formalityId={formalityRequest?.id || ""}
                    />
                  )}
                  {nodeType === "revision-node" && (
                    <RevisionSimpleContent
                      formalityId={formalityRequest?.id || ""}
                      executions={nodeExecutions || []}
                    />
                  )}
                  {nodeType === "if-node" && (
                    <IfSimpleContent execution={currentExecution!} />
                  )}
                  {nodeType === "switch-node" && (
                    <SwitchSimpleContent execution={currentExecution!} />
                  )}

                  {nodeType === "data-center-node" && (
                    <DataCenterContent execution={currentExecution!} />
                  )}

                  {nodeType === "document-generator-node" && (
                    <DocumentGeneratorContent execution={currentExecution!} />
                  )}
                  {nodeType === "notification-node" && (
                    <NotificationSimpleContent execution={currentExecution!} />
                  )}
                  {nodeType === "calculated-node" && (
                    <CalculatedSimpleContent execution={currentExecution!} />
                  )}
                  {nodeType === "assign-group-node" && (
                    <AssignGroupContent execution={currentExecution!} />
                  )}
                  {nodeType === "resolution-node" && (
                    <ResolutionContent execution={currentExecution!} />
                  )}
                  {nodeType === "expedient-node" && (
                    <ExpedienteSimpleContent execution={currentExecution!} />
                  )}
                  {/* Add other node types here as needed */}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </ScrollModal>
  );
};

export default ExecutionDetailsModal;
